import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import { featureRequestApi, phaseApi } from '../../api/endpoints';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { useProjects } from '../../context/ProjectContext';
import type { FeatureRequest, Phase } from '../../types';

interface LinkProjectDialogProps {
  open: boolean;
  request: FeatureRequest;
  onClose: () => void;
  onLinked: (updated: FeatureRequest) => void;
}

/** Dialog gắn dự án (+ giai đoạn tuỳ chọn) cho yêu cầu chưa thuộc dự án nào. */
export function LinkProjectDialog({ open, request, onClose, onLinked }: LinkProjectDialogProps): JSX.Element {
  const { projects } = useProjects();
  const [projectId, setProjectId] = useState('');
  const [phaseId, setPhaseId] = useState('');
  const [phases, setPhases] = useState<Phase[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setProjectId(request.project_id ?? '');
    setPhaseId(request.phase_id ?? '');
  }, [open, request]);

  useEffect(() => {
    if (!projectId) {
      setPhases([]);
      return;
    }
    let active = true;
    phaseApi
      .listByProject(projectId, { limit: 200 })
      .then((result) => {
        if (active) setPhases(result.items);
      })
      .catch(() => {
        if (active) setPhases([]);
      });
    return () => {
      active = false;
    };
  }, [projectId]);

  const handleProjectChange = (value: string) => {
    setProjectId(value);
    setPhaseId('');
  };

  const handleSave = async () => {
    if (!projectId) {
      notify('Vui lòng chọn dự án', 'warning');
      return;
    }
    setSaving(true);
    try {
      const updated = await featureRequestApi.linkProject(request.id, {
        project_id: projectId,
        phase_id: phaseId || null,
      });
      notify('Đã gắn dự án cho yêu cầu', 'success');
      onLinked(updated);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể gắn dự án'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Gắn dự án cho yêu cầu</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small" required>
            <InputLabel id="link-project-label">Dự án</InputLabel>
            <Select
              labelId="link-project-label"
              label="Dự án"
              value={projectId}
              onChange={(e) => handleProjectChange(e.target.value)}
            >
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" disabled={!projectId}>
            <InputLabel id="link-phase-label">Giai đoạn (tuỳ chọn)</InputLabel>
            <Select
              labelId="link-phase-label"
              label="Giai đoạn (tuỳ chọn)"
              value={phaseId}
              onChange={(e) => setPhaseId(e.target.value)}
            >
              <MenuItem value="">
                <em>Không chọn</em>
              </MenuItem>
              {phases.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Huỷ
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Đang lưu…' : 'Gắn dự án'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
